var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
/*
 * The authority types a role can be granted for are NOT listed here (CZNA-650).
 *
 * Until CZNA-650 this file held `AUTHORITY_TYPES`, a copy of the backend `RecordType` enum
 * pinned by `roleModelSync.test.ts`. That copy was the only thing deciding which types a role
 * could be granted for, and it could not follow the `vocabulary_type` registry, which an admin
 * grows at runtime: authorization in mod-czna-authorities matches a VOCABULARY_* grant, but no
 * screen could make one. The list now comes from `GET /czna-authorities/authority-types`
 * (`useAuthorityTypes`), and mod-czna-users refuses a grant outside it.
 *
 * What stays pinned is what this module still owns: the translation keys
 * `ui-czna-users.authorityType.<RecordType>` (`roleModelSync.test.ts`) and their wording
 * against ui-czna-authorities (`authorityTypeLabelParity.test.ts`). Registry types bring their
 * own labels from the server.
 */
/**
 * Display label of an authority type: a record type by this module's translations, a
 * vocabulary type by its registry label in the UI language (Czech when the locale is not
 * English), falling back to the other language and finally to the raw code.
 */
export var authorityTypeLabel = function (intl, type) {
    var _a;
    if (type.kind === "RECORD_TYPE") {
        return intl.formatMessage({
            id: "ui-czna-users.authorityType.".concat(type.code),
        });
    }
    var english = (_a = intl.locale) === null || _a === void 0 ? void 0 : _a.startsWith("en");
    var label = english
        ? type.labelEn || type.labelCs
        : type.labelCs || type.labelEn;
    return label || type.code;
};
/**
 * Splits the server list into the two sections the role screens show: record types in the
 * server's order (the one the rest of the app uses), vocabulary types sorted by label, since
 * their codes say little to an admin.
 */
export var splitAuthorityTypes = function (intl, types) { return ({
    recordTypes: types.filter(function (type) { return type.kind === "RECORD_TYPE"; }),
    vocabularyTypes: types
        .filter(function (type) { return type.kind !== "RECORD_TYPE"; })
        .sort(function (a, b) {
        return authorityTypeLabel(intl, a).localeCompare(authorityTypeLabel(intl, b), intl.locale);
    }),
}); };
/**
 * The server list plus every code the user already holds that the list no longer names — a
 * vocabulary type removed from the registry after the grant, say.
 *
 * Such a grant still exists and must stay visible and removable: hiding it would leave a role
 * nobody can see, and the backend lets it be changed or removed (it validates only a code
 * that changes). It is labelled by its raw code, the same way an unresolvable organization is.
 */
export var withAssignedTypes = function (types, assignedCodes) {
    var known = new Set(types.map(function (type) { return type.code; }));
    var extra = assignedCodes.filter(function (code, i) {
        return code !== undefined &&
            code !== "" &&
            !known.has(code) &&
            assignedCodes.indexOf(code) === i;
    });
    return extra.length
        ? __spreadArray(__spreadArray([], types, true), extra.map(function (code) { return ({ code: code, kind: "VOCABULARY" }); }), true) : types;
};
/**
 * The authority types a role screen lists for a user, in the order every role screen uses:
 * record types in the server's order, then vocabulary types by label. A held code the server
 * list no longer names (or every held code, when the list failed to load) is included by its
 * code — see {@link withAssignedTypes}.
 *
 * `unheldRecordTypes` decides whether a record type the user does not hold is listed too: the
 * user detail gives the full record-type overview, My profile (CZNA-1611) only what is held.
 * An unheld vocabulary type is never listed — the registry is ~30 types most users never get.
 */
export var roleScreenAuthorityTypes = function (intl, types, heldCodes, _a) {
    var unheldRecordTypes = _a.unheldRecordTypes;
    var held = new Set(heldCodes);
    var _b = splitAuthorityTypes(intl, withAssignedTypes(types !== null && types !== void 0 ? types : [], heldCodes)), recordTypes = _b.recordTypes, vocabularyTypes = _b.vocabularyTypes;
    return __spreadArray(__spreadArray([], recordTypes.filter(function (type) { return unheldRecordTypes || held.has(type.code); }), true), vocabularyTypes.filter(function (type) { return held.has(type.code); }), true);
};
/**
 * The STORED per-authority-type roles, mirroring
 * `mod-czna-users/.../domain/entity/enums/AuthorityTypeRole.java`, pinned by
 * `roleModelSync.test.ts`. Since CZNA-1598 nothing assigns them directly — the screens pick
 * an NK role (`nkRoles.ts`) — they only name the rows of a non-standard assignment.
 */
export var AUTHORITY_TYPE_ROLES = [
    "CATALOGER",
    "CATALOGING_SUPERVISOR",
];
/**
 * Global roles the form renders. USER is assigned by the backend to every user
 * and cannot be removed, so it is shown as a fixed, disabled checkbox; the rest
 * are editable (each behind its own permission gate — see UserForm). Mirrors
 * `mod-czna-users/.../domain/entity/enums/Role.java`, pinned by
 * `roleModelSync.test.ts`.
 */
export var FIXED_GLOBAL_ROLES = ["USER"];
export var EDITABLE_GLOBAL_ROLES = [
    "VOCABULARY_ADMIN",
    "USER_ADMIN",
    "AUTHORITY_ADMIN",
];
